import React from 'react'

const FAST_INTERVAL = 5_000 // 5 seconds
const SLOW_INTERVAL = 100_000 // 1 minute 40sec
const PLACE_HOLDER_FN = () => undefined
interface RefreshInterface {
  fast: number
  slow: number
  restartSlowTimer: () => void
  restartFastTimer: () => void
}

export const RefreshContext = React.createContext<RefreshInterface>({
  slow: 0,
  fast: 0,
  restartSlowTimer: PLACE_HOLDER_FN,
  restartFastTimer: PLACE_HOLDER_FN,
})

// This context maintain 2 counters that can be used as a dependencies on other hooks to force a periodic refresh
export const RefreshContextProvider = ({
  children,
}: {
  children: JSX.Element[] | JSX.Element
}): JSX.Element => {
  const [slow, setSlow] = React.useState(0)
  const [fast, setFast] = React.useState(0)

  const [resetFlag, setResetFlag] = React.useState(0)

  const [slowInterval, setSlowInterval] = React.useState<NodeJS.Timeout>()
  const [fastInterval, setFastInterval] = React.useState<NodeJS.Timeout>()

  const getTimer = (interval: number, setter: React.Dispatch<React.SetStateAction<number>>) => {
    return setInterval(async () => {
      setter((prev) => prev + 1)
    }, interval)
  }

  const clear = (interval: NodeJS.Timeout, includeFlag = false) => {
    clearInterval(interval)
    if (includeFlag) {
      setResetFlag((prev) => prev + 1)
    }
  }

  React.useEffect(() => {
    const fast = getTimer(FAST_INTERVAL, setFast)
    const slow = getTimer(SLOW_INTERVAL, setSlow)

    setSlowInterval(slow)
    setFastInterval(fast)

    return () => {
      clear(fast)
      clear(slow)
    }
  }, [resetFlag])

  const restartSlowTimer = () => restartTimer(slowInterval)
  const restartFastTimer = () => restartTimer(fastInterval)

  const restartTimer = (interval: NodeJS.Timeout | undefined): void => {
    if (interval) {
      clear(interval, true)
    }
  }

  return (
    <RefreshContext.Provider value={{ slow, fast, restartSlowTimer, restartFastTimer }}>
      {children}
    </RefreshContext.Provider>
  )
}

export const useRefresh = (): {
  fastRefresh: number
  slowRefresh: number
  restartFastTimer: () => void
  restartSlowTimer: () => void
} => {
  const { fast, slow, restartFastTimer, restartSlowTimer } = React.useContext(RefreshContext)
  return { fastRefresh: fast, slowRefresh: slow, restartFastTimer, restartSlowTimer }
}

export const useSlowRefresh = (): number => React.useContext(RefreshContext).slow
export const useFastRefresh = (): number => React.useContext(RefreshContext).fast
